import store from "@/store";
import {
  getJSSKDAccessToken,
  getTicket,
  getWxJSSDKSignature,
  appid
} from "@/utils/api/wx";
import { expiredChecked } from "@/utils/tool";

const DEFAULT_TIMEOUT = 10000;
const DEFAULT_RESULT = {
  success: false,
  latitude: null,
  longitude: null,
  message: "",
  type: ""
};

function isWeChatBrowser() {
  return /micromessenger/i.test(navigator.userAgent || "");
}

function normalizeLocation(latitude, longitude) {
  return {
    success: true,
    latitude: Number(latitude),
    longitude: Number(longitude),
    message: "获取经纬度成功",
    type: "success"
  };
}

function normalizeError(message, type) {
  return {
    ...DEFAULT_RESULT,
    message,
    type
  };
}

function getBrowserErrorMessage(error) {
  if (!error) {
    return normalizeError("获取经纬度失败", "error");
  }

  if (error.code === 1) {
    return normalizeError("用户未授权获取经纬度", "denied");
  }

  if (error.code === 2) {
    return normalizeError("当前无法获取经纬度", "unavailable");
  }

  if (error.code === 3) {
    return normalizeError("获取经纬度超时", "timeout");
  }

  return normalizeError(error.message || "获取经纬度失败", "error");
}

function getCurrentUrl() {
  return window.location.href.split("#")[0];
}

function configWxLocationApi() {
  return new Promise(async (resolve) => {
    if (!window.wx) {
      resolve(normalizeError("当前微信环境不支持获取经纬度", "unsupported"));
      return;
    }

    try {
      let jsToken = store.state.jsSDKAccessToken;
      if (!jsToken.access_token || expiredChecked(jsToken.expire_timestamp)) {
        const { data } = await getJSSKDAccessToken();
        if (data.errcode) {
          resolve(normalizeError(data.errmsg || "微信授权获取失败", "signature"));
          return;
        }

        jsToken = {
          access_token: data.access_token,
          expire_timestamp:
            Date.parse(new Date()) / 1000 + (parseInt(data.expires_in) - 1600)
        };
        store.commit("SET_JSSDK_ACCESS_TOKEN", jsToken);
      }

      let jsTicket = store.state.jsTicket;
      if (!jsTicket.jsapi_ticket || expiredChecked(jsTicket.expire_timestamp)) {
        const { data } = await getTicket(jsToken.access_token);
        if (data.errcode !== 0) {
          // resolve(normalizeError(data.errmsg || "微信票据获取失败", "signature"));
          return;
        }

        jsTicket = {
          jsapi_ticket: data.ticket,
          expire_timestamp:
            Date.parse(new Date()) / 1000 + (parseInt(data.expires_in) - 1600)
        };
        store.commit("SET_WX_TICKET", jsTicket);
      }

      const res = await getWxJSSDKSignature({
        ticket: jsTicket.jsapi_ticket,
        url: getCurrentUrl()
      });

      if (!res.success) {
        resolve(normalizeError("微信签名获取失败", "signature"));
        return;
      }

      window.wx.config({
        debug: false,
        appId: appid,
        timestamp: res.data.timestamp,
        nonceStr: res.data.noncestr,
        signature: res.data.signature,
        jsApiList: ["getLocation"]
      });

      window.wx.ready(() => {
        resolve(null);
      });

      window.wx.error((err) => {
        resolve(
          normalizeError((err && err.errMsg) || "微信权限配置失败", "signature")
        );
      });
    } catch (error) {
      resolve(normalizeError(error.message || "微信授权获取失败", "signature"));
    }
  });
}

function getWxLocation(options) {
  return new Promise(async (resolve) => {
    const configError = await configWxLocationApi();
    if (configError) {
      resolve(configError);
      return;
    }

    window.wx.getLocation({
      type: options.coordType || "wgs84",
      success(res) {
        resolve(normalizeLocation(res.latitude, res.longitude));
      },
      cancel() {
        resolve(normalizeError("用户未授权获取经纬度", "denied"));
      },
      fail(error) {
        const errMsg = error && error.errMsg ? error.errMsg : "获取经纬度失败";
        const isDenied = /deny|denied|cancel/i.test(errMsg);
        resolve(normalizeError(errMsg, isDenied ? "denied" : "error"));
      }
    });
  });
}

function getH5Location(options) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(normalizeError("当前浏览器不支持获取经纬度", "unsupported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(
          normalizeLocation(
            position.coords.latitude,
            position.coords.longitude
          )
        );
      },
      (error) => {
        resolve(getBrowserErrorMessage(error));
      },
      {
        enableHighAccuracy: options.enableHighAccuracy !== false,
        timeout: options.timeout || DEFAULT_TIMEOUT,
        maximumAge: options.maximumAge || 0
      }
    );
  });
}

/**
 * 通用获取经纬度方法。
 * 微信内浏览器优先使用微信 JS-SDK，非微信浏览器使用 H5 Geolocation。
 *
 * @param {Object} options
 * @param {number} options.timeout H5 定位超时时间，默认 10000ms
 * @param {boolean} options.enableHighAccuracy H5 是否开启高精度定位，默认 true
 * @param {number} options.maximumAge H5 缓存位置最大有效时间，默认 0
 * @param {string} options.coordType 微信坐标类型，默认 wgs84，可传 gcj02
 * @returns {Promise<{success: boolean, latitude: number|null, longitude: number|null, message: string, type: string}>}
 */
export function getLocation(options = {}) {
  if (isWeChatBrowser()) {
    return getWxLocation(options);
  }

  return getH5Location(options);
}

export default getLocation;
